import { useNavigate } from "react-router-dom";
import { useAuth } from "@/components/feature/AuthContext";
import CrmLayout from "@/components/feature/CrmLayout";

// ── Settings card data ──
type SettingsCard = {
  icon: string;
  label: string;
  description: string;
  path: string;
  colorClass: string;
};

type SettingsSection = {
  title: string;
  cards: SettingsCard[];
};

const settingsSections: SettingsSection[] = [
  {
    title: "Company & Team",
    cards: [
      {
        icon: "ri-building-2-line",
        label: "Company Profile",
        description: "Update company name, logo, address and business details",
        path: "/settings/company",
        colorClass: "bg-indigo-100 text-indigo-600",
      },
      {
        icon: "ri-group-line",
        label: "Users",
        description: "Add and manage team members, reset passwords, activate accounts",
        path: "/settings/users",
        colorClass: "bg-blue-100 text-blue-600",
      },
      {
        icon: "ri-shield-user-line",
        label: "Positions & Roles",
        description: "Create positions, set permissions and data access for each level",
        path: "/settings/positions",
        colorClass: "bg-purple-100 text-purple-600",
      },
      {
        icon: "ri-map-pin-line",
        label: "Branches",
        description: "Add office locations and assign users and leads to each branch",
        path: "/settings/branchs",
        colorClass: "bg-amber-100 text-amber-600",
      },
      {
        icon: "ri-organization-chart",
        label: "Team Structure",
        description: "View org chart, assign managers and build your team hierarchy",
        path: "/settings/teams",
        colorClass: "bg-teal-100 text-teal-600",
      },
    ],
  },
  {
    title: "Sales Configuration",
    cards: [
      {
        icon: "ri-building-4-line",
        label: "Industry Config",
        description: "Set pipeline stages, custom lead fields and lead source settings",
        path: "/settings/industry",
        colorClass: "bg-green-100 text-green-600",
      },
      {
        icon: "ri-stack-line",
        label: "Project Settings",
        description: "Define selling types and inventory fields for your projects",
        path: "/settings/projects",
        colorClass: "bg-amber-100 text-amber-600",
      },
      {
        icon: "ri-star-line",
        label: "Lead Scoring",
        description: "Configure automatic scoring rules, thresholds and auto-actions",
        path: "/settings/scoring",
        colorClass: "bg-orange-100 text-orange-600",
      },
      {
        icon: "ri-list-check",
        label: "Assignment Rules",
        description: "Set up auto-assignment, round robin, escalation and manual reassignment",
        path: "/settings/assignment",
        colorClass: "bg-red-100 text-red-600",
      },
    ],
  },
  {
    title: "Automation & AI",
    cards: [
      {
        icon: "ri-flow-chart",
        label: "Workflows",
        description: "Build automated WhatsApp bot flows, follow-up sequences and triggers",
        path: "/settings/workflows",
        colorClass: "bg-violet-100 text-violet-600",
      },
      {
        icon: "ri-robot-2-line",
        label: "Bot Identity",
        description: "Configure bot name, persona, language and source activation",
        path: "/settings/ai-agent",
        colorClass: "bg-pink-100 text-pink-600",
      },
      {
        icon: "ri-book-open-line",
        label: "Knowledge Base",
        description: "Add FAQs and product info for bot to answer lead questions",
        path: "/settings/knowledge-base",
        colorClass: "bg-cyan-100 text-cyan-600",
      },
    ],
  },
  {
    title: "Channels & Integrations",
    cards: [
      {
        icon: "ri-plug-line",
        label: "Integrations",
        description: "Connect IndiaMart, JustDial, WhatsApp, telephony and more",
        path: "/integrations",
        colorClass: "bg-emerald-100 text-emerald-600",
      },
      {
        icon: "ri-global-line",
        label: "Web Forms",
        description: "Create embeddable forms for your website to capture leads",
        path: "/lead-capture",
        colorClass: "bg-sky-100 text-sky-600",
      },
    ],
  },
];

// ── Quick access pills ──
const quickAccessPills = [
  { icon: "ri-group-line", label: "Users", path: "/settings/users" },
  { icon: "ri-list-check", label: "Assignment Rules", path: "/settings/assignment" },
  { icon: "ri-flow-chart", label: "Workflows", path: "/settings/workflows" },
  { icon: "ri-building-4-line", label: "Industry Config", path: "/settings/industry" },
  { icon: "ri-organization-chart", label: "Team Structure", path: "/settings/teams" },
  { icon: "ri-robot-2-line", label: "Bot Identity", path: "/settings/ai-agent" },
];

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";

  return (
    <CrmLayout title="Settings">
      <div className="p-5 md:p-6 max-w-7xl mx-auto">
        {/* ── Page header ── */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage your CRM configuration
          </p>
        </div>

        {/* ── Quick Access Bar ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-8">
          <p className="text-sm font-medium text-gray-700 mb-3">Quick Access</p>
          <div className="flex items-center gap-2 flex-wrap">
            {quickAccessPills
              .filter((pill) => {
                if (pill.path === "/settings/positions") return isSuperAdmin;
                if (pill.path === "/settings/industry") return isSuperAdmin;
                return true;
              })
              .map((pill) => (
                <button
                  key={pill.label}
                  onClick={() => navigate(pill.path)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-xs text-gray-700 hover:bg-indigo-50 hover:border-indigo-300 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className={`${pill.icon} text-sm w-4 h-4 flex items-center justify-center`} />
                  {pill.label}
                </button>
              ))}
          </div>
        </div>

        {/* ── Settings Sections ── */}
        <div className="space-y-10">
          {settingsSections.map((section) => {
            const visibleCards = section.cards.filter((card) => {
              if (card.path === "/settings/positions") return isSuperAdmin;
              if (card.path === "/settings/industry") return isSuperAdmin;
              return true;
            });

            if (visibleCards.length === 0) return null;

            return (
              <section key={section.title}>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  {section.title}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {visibleCards.map((card) => (
                    <button
                      key={card.label}
                      onClick={() => navigate(card.path)}
                      className="group relative bg-white rounded-xl border border-gray-200 p-5 text-left hover:border-indigo-300 hover:shadow-sm transition-all duration-200 cursor-pointer"
                    >
                      <div
                        className={`w-11 h-11 rounded-xl ${card.colorClass} flex items-center justify-center text-xl mb-3`}
                      >
                        <i className={`${card.icon} w-5 h-5 flex items-center justify-center`} />
                      </div>
                      <h3 className="text-sm font-semibold text-gray-900">
                        {card.label}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                        {card.description}
                      </p>
                      <div className="absolute top-4 right-4 text-gray-300 group-hover:text-indigo-400 transition-colors">
                        <i className="ri-arrow-right-s-line text-lg w-4 h-4 flex items-center justify-center" />
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </CrmLayout>
  );
}